/**
 * MediaRecorder wrapper. One module, both devices — the laptop passes
 * `source: "webcam"`, the phone passes `source: "phone"`; nothing else differs.
 *
 * Chunking: `MediaRecorder.start(timeslice)` emits a blob every `timeslice` ms.
 * Only the FIRST blob of a recorder SESSION carries the WebM header; the rest
 * are fragments. The admin player concatenates a session's chunks in
 * `chunk_index` order and decodes them as one blob, which is why
 * `chunk_index` must never be reused or skipped, why chunk 0 must not be
 * dropped, and why every chunk carries the session that produced it.
 *
 * There is more than one session per recording now: this recorder restarts
 * itself after a stall, and recording spans the setup page and the exam
 * runner. Two sessions can be adjacent in time with no gap between them, so
 * the player splits runs on the session number rather than on a hole in the
 * clock.
 *
 * Every chunk lands in IndexedDB before an upload is attempted, so nothing is
 * lost to a network drop, a reload, or a crashed tab.
 */
import type { RecordingSource } from "@exam/shared";
import type { ServerClock } from "@/lib/exam/serverClock";
import {
  allocateChunkIndex,
  allocateSessionIdx,
  enqueueChunk,
} from "./chunkStore";
import { ChunkUploader, type UploaderStatus } from "./uploader";

export const CHUNK_DURATION_MS = 10_000;

/**
 * Hard ceiling on how long `stop()` waits for the final `onstop`/`dataavailable`.
 * A MediaRecorder whose track already died can go silent WITHOUT ever firing
 * `onstop` — awaiting it forever is what made "finishing upload" hang at submit.
 * After this, finalize continues regardless; whatever is queued still uploads.
 */
const STOP_TIMEOUT_MS = 4_000;

/**
 * Bounded window for the final upload drain at submit. A single hung `fetch`
 * (connection open, no response) inside the drain is what makes "finishing
 * upload" spin forever — this caps it. Anything still queued stays in
 * IndexedDB; submit finalizes regardless.
 */
const FINALIZE_UPLOAD_TIMEOUT_MS = 8_000;

/**
 * How long a recorder may produce nothing before it is treated as stalled,
 * as a multiple of the chunk period. Two and a half periods is long enough
 * that a slow encoder on a loaded machine is not mistaken for a failure.
 *
 * THIS IS THE GAP THAT LOST HALF AN EXAM. A MediaRecorder can stop emitting
 * `dataavailable` while its `state` stays "recording" and its track stays
 * "live" — no `onerror`, no `onstop`, no track `ended`, nothing to hook. Every
 * watchdog in this app watched the TRACK, so all of them saw a healthy camera
 * and said so, while the recording had already ended. In the attempt that
 * exposed it, the webcam produced a clean, gapless run of chunks and then
 * simply stopped at 4:17; the screen capture stopped at 4:39; the student
 * carried on answering for another seven minutes and submitted normally, and
 * not one event was raised. The only thing that can catch that is watching for
 * the ABSENCE of output, which is what this does.
 */
const STALL_AFTER_CHUNKS = 2.5;

/**
 * Consecutive stalls before we stop trying to restart the MediaRecorder and
 * ask the owner for a whole new stream. One restart handles a transient
 * encoder fault; a second means the stream itself is the problem, however
 * healthy its track claims to be.
 */
const MAX_RESTARTS_BEFORE_REACQUIRE = 2;

/**
 * Consecutive failed writes to the local queue before the recording counts as
 * broken. Two in a row is not a blip: nothing since the last success has been
 * kept, and the student needs to know while there is still exam left to save.
 */
const STORE_FAILURES_BEFORE_STALL = 2;

/** Ordered by preference; the first supported one wins. */
const MIME_CANDIDATES = [
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm",
  // Safari / iOS — not a target, but failing over beats failing hard.
  "video/mp4",
];

export function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t));
}

export interface RecorderOptions {
  attemptId: string;
  source: RecordingSource;
  stream: MediaStream;
  /** Server-anchored clock — chunk timestamps must not use the local clock. */
  clock: ServerClock;
  deviceToken?: string;
  chunkDurationMs?: number;
  /**
   * A mid-range Android phone will drop frames at a high bitrate, and the
   * upload has to keep up over mobile data. 600 kbps is legible for proctoring.
   */
  videoBitsPerSecond?: number;
  onStatus?: (status: RecorderStatus) => void;
  /**
   * Called when the recorder cannot heal itself because the underlying video
   * track has ended (the camera died). The owner should re-acquire the camera;
   * a fresh stream rebuilds this recorder and recording resumes. Without this,
   * a track that ends mid-exam silently ends the recording for good — which is
   * exactly the laptop-webcam failure this recorder now recovers from.
   */
  onStreamLost?: () => void;
  /**
   * Called when the recorder has produced nothing for long enough to count as
   * broken. The owner logs a proctor event: a recording that stops mid-exam is
   * exactly what the reviewer needs to be told about, and it must be recorded
   * at the moment it happens rather than inferred from a gap afterwards.
   */
  onStall?: (info: {
    silentForMs: number;
    willRestart: boolean;
    /** What went quiet: the encoder, or the local store that keeps its output. */
    reason: "encoder" | "storage";
  }) => void;
}

export interface RecorderStatus {
  recording: boolean;
  /** Chunks produced this session (not the same as chunks uploaded). */
  chunksProduced: number;
  upload: UploaderStatus;
  error: string | null;
  /**
   * True while the recorder is producing nothing despite being told to record.
   * Distinct from an upload backlog: this means there is no footage to upload,
   * which the queue's own numbers can never reveal (see `uploadHealth.ts`).
   */
  stalled: boolean;
  /** Wall-clock ms of the last chunk produced; null before the first. */
  lastChunkAtMs: number | null;
}

const IDLE_UPLOAD_STATUS: UploaderStatus = {
  pending: 0,
  pendingBytes: 0,
  uploading: false,
  failures: 0,
  uploaded: 0,
  refused: 0,
  lastErrorMessage: null,
  lastUploadAtMs: null,
  wedgeRecoveries: 0,
};

export class AttemptRecorder {
  private readonly options: RecorderOptions;
  private readonly uploader: ChunkUploader;
  private readonly chunkDurationMs: number;

  private recorder: MediaRecorder | null = null;
  private chunksProduced = 0;
  private error: string | null = null;
  private uploadStatus: UploaderStatus = IDLE_UPLOAD_STATUS;
  /**
   * True between `start()` and `stop()`. The self-heal watchdog only fires while
   * this is set, so a graceful stop is never mistaken for a crash to restart.
   */
  private intendedRecording = false;
  /** The video track we are watching for an unexpected `ended`. */
  private watchedTrack: MediaStreamTrack | null = null;
  private readonly onTrackEnded = () => this.recover("track ended");
  /** Watchdog for the silent-stall case described at STALL_AFTER_CHUNKS. */
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private lastChunkAtMs: number | null = null;
  private stalled = false;
  private restarts = 0;
  /** Consecutive failed writes to the chunk queue. See handleChunk. */
  private storeFailures = 0;
  /**
   * Which MediaRecorder session the next chunk belongs to.
   *
   * Allocated from the chunk store every time a new MediaRecorder is started,
   * because only the first blob of a session carries the WebM header. The
   * admin player splits playable runs on a change of this — without it, a
   * recorder that restarts itself (the stall watchdog does exactly that,
   * immediately, leaving no gap in time) puts a header in the middle of a run
   * and makes everything after it undecodable.
   *
   * Persisted rather than counted in memory because recording now spans two
   * pages: setup and the exam are different recorder instances, and a fresh
   * instance counting from zero would claim its chunks belong with the
   * previous session's.
   *
   * Held per MediaRecorder, in the `ondataavailable` closure — NOT on the
   * instance. See the note in `start()`.
   */

  constructor(options: RecorderOptions) {
    this.options = options;
    this.chunkDurationMs = options.chunkDurationMs ?? CHUNK_DURATION_MS;
    this.uploader = new ChunkUploader({
      attemptId: options.attemptId,
      source: options.source,
      deviceToken: options.deviceToken,
      onStatus: (status) => {
        this.uploadStatus = status;
        this.emit();
      },
    });
  }

  get isRecording(): boolean {
    return this.recorder?.state === "recording";
  }

  start(): void {
    if (this.isRecording) return;
    this.intendedRecording = true;

    const mimeType = pickMimeType();
    if (!mimeType) {
      this.error = "This browser cannot record video.";
      this.emit();
      return;
    }

    const track = this.options.stream.getVideoTracks()[0];
    if (!track || track.readyState === "ended") {
      // Nothing to record from. Ask the owner to re-acquire the camera; a fresh
      // stream will rebuild this recorder.
      this.error = "The camera stopped. Trying to reconnect…";
      this.emit();
      this.options.onStreamLost?.();
      return;
    }

    // A new MediaRecorder is a new session, and its first blob carries a fresh
    // header. Allocated here so the handler below can close over THIS
    // session's promise; a rejection is deliberately not swallowed — see
    // `handleChunk`, which treats it as a storage failure rather than
    // defaulting to 0. Zero is the worst possible fallback: it is the one
    // value guaranteed to collide with the setup page's session and merge an
    // exam recording into it.
    const session = allocateSessionIdx(
      this.options.attemptId,
      this.options.source,
    );

    try {
      const recorder = new MediaRecorder(this.options.stream, {
        mimeType,
        videoBitsPerSecond: this.options.videoBitsPerSecond ?? 600_000,
        audioBitsPerSecond: 32_000,
      });

      /**
       * The session and the chunk clock are CLOSED OVER, not read off the
       * instance.
       *
       * `stop()` only queues the final `dataavailable`, so the dying session's
       * tail blob fires after `start()` has already replaced the instance
       * fields. Reading them at that point stamps a headerless fragment as the
       * FIRST chunk of the next session — which makes the whole of that run
       * undecodable, silently, which is the exact failure sessions were added
       * to prevent. Awaiting a promise is not the same as capturing it.
       */
      let chunkStartedMs = Math.round(this.options.clock.now());
      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;
        const startedMs = chunkStartedMs;
        chunkStartedMs = Math.round(this.options.clock.now());
        void this.handleChunk(event.data, {
          session,
          startedMs,
          durationMs: Math.max(0, chunkStartedMs - startedMs),
        });
      };

      // A MediaRecorder can die two ways: an explicit error, or going silent
      // when its track ends (often WITHOUT firing `onstop`). We watch both the
      // recorder AND the track, and restart if we still intend to record.
      recorder.onerror = () => this.recover("recorder error");
      recorder.onstop = () => this.recover("recorder stopped");

      this.watchTrack(track);

      recorder.start(this.chunkDurationMs);
      this.recorder = recorder;
      this.error = null;

      // Reset the silence clock here, not on the first chunk: the gap between
      // start() and the first `dataavailable` is one chunk period, and a
      // recorder that never produces its FIRST chunk is just as broken as one
      // that stops after fifty.
      this.lastChunkAtMs = Date.now();
      this.startWatchdog();

      this.uploader.start();
      this.emit();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Could not start recording.";
      this.emit();
    }
  }

  /**
   * Self-heal: called when the recorder stops or errors, or the track ends,
   * while we still intend to be recording. If the track is alive, we restart the
   * MediaRecorder on the same stream (chunk indices continue, so no video is
   * overwritten). If the track is dead, we cannot fix it here — the owner
   * re-acquires the camera, which rebuilds this recorder with a fresh stream.
   */
  private recover(reason: string): void {
    if (!this.intendedRecording) return; // a graceful stop, not a crash
    if (this.recorder && this.recorder.state === "recording") return; // false alarm

    const track = this.options.stream.getVideoTracks()[0];
    if (!track || track.readyState === "ended") {
      console.warn(
        `[recorder] ${this.options.source} lost its camera (${reason}) — re-acquiring`,
      );
      this.error = "The camera stopped. Trying to reconnect…";
      this.emit();
      this.options.onStreamLost?.();
      return;
    }

    console.warn(
      `[recorder] ${this.options.source} recorder ${reason} while recording — restarting`,
    );
    this.recorder = null;
    this.start(); // intendedRecording is still true; continues the sequence
  }

  /**
   * Polls for the absence of output. Runs at the chunk period, so a stall is
   * noticed within about one chunk of the threshold.
   */
  private startWatchdog(): void {
    this.stopWatchdog();
    this.watchdog = setInterval(() => {
      if (!this.intendedRecording) return;

      const last = this.lastChunkAtMs;
      if (last === null) return;

      const silentFor = Date.now() - last;
      if (silentFor < this.chunkDurationMs * STALL_AFTER_CHUNKS) return;

      // A storage stall is already being reported by handleChunk, and the
      // encoder is fine — restarting it would not help.
      if (this.storeFailures >= STORE_FAILURES_BEFORE_STALL) return;

      this.restarts += 1;
      const willRestart = this.restarts <= MAX_RESTARTS_BEFORE_REACQUIRE;

      console.warn(
        `[recorder] ${this.options.source} produced nothing for ${Math.round(
          silentFor / 1000,
        )}s — ${willRestart ? "restarting" : "re-acquiring the stream"}`,
      );
      this.error = "Recording stopped unexpectedly. Restarting…";
      this.options.onStall?.({
        silentForMs: silentFor,
        willRestart,
        reason: "encoder",
      });
      this.emit();

      if (willRestart) {
        // The track may still claim to be live; tear the recorder down anyway,
        // because it is the thing that has gone quiet.
        try {
          this.recorder?.stop();
        } catch {
          // Already dead. Restarting is still the right move.
        }
        this.recorder = null;
        // Re-arm rather than latch. Leaving `stalled` set until a chunk
        // arrived meant a restart that ALSO produced nothing was never
        // noticed: `restarts` could not pass 1, so the escalation to
        // `onStreamLost` — the only thing that re-acquires the camera — was
        // unreachable. Only a real chunk clears `restarts`.
        this.stalled = false;
        this.lastChunkAtMs = Date.now();
        this.start();
      } else {
        this.options.onStreamLost?.();
      }
    }, this.chunkDurationMs);
  }

  private stopWatchdog(): void {
    if (this.watchdog !== null) clearInterval(this.watchdog);
    this.watchdog = null;
  }

  private watchTrack(track: MediaStreamTrack): void {
    if (this.watchedTrack === track) return;
    this.unwatchTrack();
    this.watchedTrack = track;
    track.addEventListener("ended", this.onTrackEnded);
  }

  private unwatchTrack(): void {
    this.watchedTrack?.removeEventListener("ended", this.onTrackEnded);
    this.watchedTrack = null;
  }

  /**
   * Stops the recorder and flushes the final partial chunk. Cannot hang: if the
   * final `onstop` never arrives (a track that already died), it resolves on a
   * timeout so submit-finalize always completes.
   */
  async stop(): Promise<void> {
    this.intendedRecording = false; // disarm the self-heal watchdog
    this.stopWatchdog();
    this.unwatchTrack();

    const recorder = this.recorder;
    if (!recorder || recorder.state === "inactive") {
      this.recorder = null;
      this.emit();
      return;
    }

    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      recorder.onstop = finish;
      // Flush the tail explicitly, then stop. A timeout guarantees finalize
      // never blocks on a chunk that cannot arrive.
      try {
        if (recorder.state === "recording") recorder.requestData();
        recorder.stop();
      } catch {
        finish();
      }
      setTimeout(finish, STOP_TIMEOUT_MS);
    });

    this.recorder = null;
    this.emit();
  }

  /**
   * Drains whatever is queued, then stops the background uploader. Bounded: a
   * hung upload cannot make submit's "finishing upload" spin forever — after
   * the window, finalize continues and anything unsent stays in IndexedDB.
   */
  async finishUploads(): Promise<void> {
    await Promise.race([
      this.uploader.drain(),
      new Promise<void>((resolve) =>
        setTimeout(resolve, FINALIZE_UPLOAD_TIMEOUT_MS),
      ),
    ]);
    this.uploader.stop();
  }

  /** Stops everything. Queued chunks survive in IndexedDB. */
  async dispose(): Promise<void> {
    await this.stop();
    this.uploader.stop();
  }

  /**
   * `boundary` comes from the handler that produced the blob, never from this
   * instance: by the time a tail chunk arrives, the fields have already moved
   * on to the next session.
   */
  private async handleChunk(
    blob: Blob,
    boundary: {
      session: Promise<number>;
      startedMs: number;
      durationMs: number;
    },
  ): Promise<void> {
    const { startedMs, durationMs } = boundary;

    try {
      // Both awaits are inside the try: a failed session allocation must land
      // in the storage-failure path below, where it is reported and eventually
      // raises a stall, rather than quietly becoming session 0.
      const [chunkIndex, sessionIdx] = await Promise.all([
        allocateChunkIndex(this.options.attemptId, this.options.source),
        boundary.session,
      ]);

      await enqueueChunk({
        attemptId: this.options.attemptId,
        source: this.options.source,
        chunkIndex,
        sessionIdx,
        startedMs,
        durationMs,
        blob,
        queuedAtMs: Date.now(),
      });

      // Proof of life is a chunk that is SAVED, not one that was produced.
      // Clearing the stall before the write meant a store that rejected every
      // chunk — quota exhaustion, which a long upload backlog causes — left
      // the watchdog satisfied, `stalled` false and the queue empty, so upload
      // health read "ok" while nothing whatsoever was being kept. That is the
      // original failure exactly, moved one function along.
      this.lastChunkAtMs = Date.now();
      this.storeFailures = 0;
      if (this.stalled) {
        this.stalled = false;
        this.restarts = 0;
        this.error = null;
      }

      this.chunksProduced += 1;
      this.emit();
      void this.uploader.drain();
    } catch (err) {
      // Losing a chunk to a storage failure must not stop the recording — the
      // next may well succeed — but it must not be quiet either. Consecutive
      // failures mean nothing is being kept, which is reported as a stall so
      // the student is told and the owner can react.
      console.error("[recorder] could not queue chunk", err);
      this.storeFailures += 1;
      this.error = "A video segment could not be saved on this device.";

      if (this.storeFailures >= STORE_FAILURES_BEFORE_STALL && !this.stalled) {
        this.stalled = true;
        this.options.onStall?.({
          silentForMs: Date.now() - (this.lastChunkAtMs ?? Date.now()),
          willRestart: false,
          reason: "storage",
        });
      }
      this.emit();
    }
  }

  private emit(): void {
    this.options.onStatus?.({
      recording: this.isRecording,
      chunksProduced: this.chunksProduced,
      upload: this.uploadStatus,
      error: this.error,
      stalled: this.stalled,
      lastChunkAtMs: this.lastChunkAtMs,
    });
  }
}
